import {
  BedTwoTone,
  LocalActivityTwoTone,
  PersonTwoTone,
  PhoneTwoTone,
  ReceiptLongTwoTone,
  TourTwoTone,
} from '@mui/icons-material';
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserCheckPermission } from '../hooks/useUserCheckPermission';

export function OrderSummary(props: {
  pnr: string;
  orderNumber: string;
  orderDate: Date;
  tourInfo?: string | null;
  tourStartDate?: Date | null;
  bedType: string;
  guideInfo?: { fullName: string; phoneNumber: string }[];
  onTransferTourClick?: (open: boolean) => void;
  onBedTypeChangeClick?: (open: boolean) => void;
  price?: {
    value: number;
    currency: string;
  };
}) {
  const { t } = useTranslation();
  const [phoneMenuAnchor, setPhoneMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const phoneMenuOpen = Boolean(phoneMenuAnchor);
  const { can } = useUserCheckPermission();

  return (
    <List dense>
      <ListItem>
        <ListItemIcon>
          <LocalActivityTwoTone />
        </ListItemIcon>
        <ListItemText
          primary={props.pnr}
          secondary={t('orderDetailPage.pnr')}
        />
      </ListItem>

      <ListItem>
        <ListItemIcon>
          <ReceiptLongTwoTone />
        </ListItemIcon>
        <ListItemText
          primary={t('orderDetailPage.orderNumber', {
            orderNumber: props.orderNumber,
          })}
          secondary={
            <>
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                display="block"
              >
                {format(props.orderDate, 'dd.MM.yyyy HH:mm')}
              </Typography>
            </>
          }
        />
      </ListItem>
      {props.tourInfo && props.tourStartDate && (
        <ListItem>
          <ListItemIcon>
            <TourTwoTone />
          </ListItemIcon>
          <ListItemText
            primary={props.tourInfo}
            secondary={
              <>
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  display="block"
                >
                  {format(props.tourStartDate, 'dd.MM.yyyy HH:mm')}
                </Typography>
                {can('updateMemberSalesIndividualTourTransfer') &&
                  props.onTransferTourClick && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="textSecondary"
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => {
                        props.onTransferTourClick &&
                          props.onTransferTourClick(true);
                      }}
                    >
                      {t('orderDetailPage.transferTourButton')}
                    </Typography>
                  )}
              </>
            }
          />
        </ListItem>
      )}
      <ListItem>
        <ListItemIcon>
          <BedTwoTone />
        </ListItemIcon>
        <ListItemText
          primary={props.bedType}
          secondary={
            can('updateMemberSalesIndividualRoomType') &&
            props.onBedTypeChangeClick && (
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  props.onBedTypeChangeClick &&
                    props.onBedTypeChangeClick(true);
                }}
              >
                {t('orderDetailPage.changeBedTypeButton')}
              </Typography>
            )
          }
        />
      </ListItem>
      {props.guideInfo && props.guideInfo.length > 0 && (
        <ListItem>
          <ListItemIcon>
            <PersonTwoTone />
          </ListItemIcon>
          <ListItemText
            primary={(props.guideInfo || [])
              .map((guide) => guide.fullName)
              .join(', ')}
            secondary={t('orderDetailPage.groupLeader')}
          />
          <ListItemSecondaryAction>
            <Tooltip title={t('orderDetailPage.callGroupLeader')}>
              <IconButton
                color="primary"
                id="phone-menu-btn"
                aria-controls={phoneMenuOpen ? 'phone-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={phoneMenuOpen ? 'true' : undefined}
                onClick={(e) => setPhoneMenuAnchor(e.currentTarget)}
              >
                <PhoneTwoTone />
              </IconButton>
            </Tooltip>
          </ListItemSecondaryAction>
          <Menu
            id="phone-menu"
            anchorEl={phoneMenuAnchor}
            open={phoneMenuOpen}
            onClose={() => setPhoneMenuAnchor(null)}
            MenuListProps={{
              'aria-labelledby': 'phone-menu-btn',
            }}
          >
            {props.guideInfo?.map((g) => (
              <MenuItem
                key={g.phoneNumber}
                onClick={() => setPhoneMenuAnchor(null)}
                component="a"
                href={`tel:${g.phoneNumber}`}
              >
                <ListItemIcon>
                  <PersonTwoTone />
                </ListItemIcon>
                <ListItemText primary={g.fullName} secondary={g.phoneNumber} />
              </MenuItem>
            ))}
          </Menu>
        </ListItem>
      )}
    </List>
  );
}
